import React, {
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import DENTModalOverviewToothSelect from './DENTModalOverviewToothSelect';
import styles from './DENTToothDetailModal.module.scss';
import DENTModalOverviewGeneral from '../DENTModalOverviewGeneral/DENTModalOverviewGeneral';
import { ToothDetailContentType } from '../../Enums/ToothDetailContentType.enum';
import DENTModalOverviewPathology from '../DENTModalOverviewPathology/DENTModalOverviewPathology';
import { ToothView } from '../../Enums/ToothView.enum';
import DENTTooth from '../Tooth/DENTTooth';
import { Scale } from '../../../Base/Interfaces/Scale.interface';
import { Size } from '../../../../Enums/Size.enum';
import Modal from '../../../../Components/Modal/Modal';
import { useDENTContext } from '../../Services/DENTContext';
import DENTModalOverviewPeriodontal from '../DENTModalOverviewPeriodontal/DENTModalOverviewPeriodontal';
import DENTModalOverviewRestoration from '../DENTModalOverviewRestoration/DENTModalOverviewRestoration';
import DENTToothDetailCloseConfirmationModal from './DENTToothDetailCloseConfirmationModal';
import hasToothChanges from '../../Helpers/hasToothChanges';
import { Tooth } from '../../../../graphql/graphql';

interface Props {
	onClose: () => void;
}

const getToothViewsByNumber = (toothNumber: number): ToothView[] => {
	if (
		(toothNumber >= 11 && toothNumber <= 28) ||
		(toothNumber >= 51 && toothNumber <= 65)
	) {
		return [ToothView.BUCCAL, ToothView.INCISAL, ToothView.PALATAL];
	}
	return [ToothView.LINGUAL, ToothView.INCISAL, ToothView.BUCCAL];
};

const DENTToothDetailModal = ({ onClose }: Props) => {
	const {
		activeTooth,
		originalActiveTooth,
		setActiveTooth,
		toothDetailContentType,
		setToothDetailContentType,
	} = useDENTContext();
	const containerRef = useRef<HTMLDivElement | null>(null);
	const [scale, setScale] = useState<Scale>({ horizontal: 1, vertical: 1 });
	const [isClosingModalOpened, setIsClosingModalOpened] = useState(false);
	const [pendingToothChange, setPendingToothChange] = useState<Tooth | null>(
		null,
	);

	useEffect(() => {
		if (toothDetailContentType !== ToothDetailContentType.PERIODONTAL) {
			setToothDetailContentType(ToothDetailContentType.GENERAL);
		}
	}, [activeTooth?.number]);

	const handleCloseModal = () => {
		setIsClosingModalOpened(false);
		if (toothDetailContentType === ToothDetailContentType.GENERAL) {
			return onClose();
		}
		setActiveTooth(originalActiveTooth);
		setToothDetailContentType(ToothDetailContentType.GENERAL);
	};

	const handleOpenConfirmationModal = () => {
		if (hasToothChanges(activeTooth, originalActiveTooth)) {
			setIsClosingModalOpened(true);
		} else {
			handleCloseModal();
		}
	};

	const handleToothChange = (tooth: Tooth) => {
		if (toothDetailContentType === ToothDetailContentType.GENERAL) {
			setActiveTooth(tooth);
		} else {
			if (hasToothChanges(activeTooth, originalActiveTooth)) {
				setPendingToothChange(tooth);
				setIsClosingModalOpened(true);
			} else {
				setActiveTooth(tooth);
				setToothDetailContentType(ToothDetailContentType.GENERAL);
			}
		}
	};

	const handleConfirmToothChange = () => {
		if (pendingToothChange) {
			setActiveTooth(pendingToothChange);
			setToothDetailContentType(ToothDetailContentType.GENERAL);
			setPendingToothChange(null);
		}
		setIsClosingModalOpened(false);
	};

	const selectedContentComponent = useMemo(() => {
		if (!activeTooth) {
			return null;
		}

		switch (toothDetailContentType) {
			case ToothDetailContentType.GENERAL:
				return (
					<DENTModalOverviewGeneral
						onChangeContentType={setToothDetailContentType}
						onClose={handleCloseModal}
					/>
				);
			case ToothDetailContentType.PATHOLOGY:
				return (
					<DENTModalOverviewPathology onClose={handleOpenConfirmationModal} />
				);
			case ToothDetailContentType.RESTORATION:
				return (
					<DENTModalOverviewRestoration onClose={handleOpenConfirmationModal} />
				);
			case ToothDetailContentType.PERIODONTAL:
				return (
					<DENTModalOverviewPeriodontal onClose={handleOpenConfirmationModal} />
				);
			default:
				return null;
		}
	}, [toothDetailContentType, activeTooth]);

	const calculateScale = () => {
		if (!containerRef.current) return;
		// @ts-ignore
		const content: { clientWidth: number; clientHeight: number } =
			containerRef.current;
		setScale({
			horizontal: 250 / content.clientWidth,
			vertical: 1300 / content.clientHeight,
		});
	};

	useLayoutEffect(() => {
		calculateScale();
	}, [containerRef]);

	useEffect(() => {
		// @ts-ignore
		window.addEventListener('resize', calculateScale);
		return () => {
			// @ts-ignore
			window.removeEventListener('resize', calculateScale);
		};
	}, []);

	if (!activeTooth) {
		return <></>;
	}

	return (
		<Modal opened size={Size.FULLSCREEN}>
			<Modal.Content
				contentStyle={{ height: '100%', padding: 0 }}
				contentScroll>
				<div className={styles.container}>
					<DENTModalOverviewToothSelect onToothChange={handleToothChange} />
					<div className={styles.toothWrapper} ref={containerRef}>
						<DENTTooth
							data={activeTooth}
							views={getToothViewsByNumber(activeTooth.number)}
							scale={scale}
							showToothNumber={false}
							showToothGrid
							showToothEndodontic={false}
						/>
					</div>
					{selectedContentComponent}
				</div>
				{isClosingModalOpened && (
					<DENTToothDetailCloseConfirmationModal
						onConfirm={
							pendingToothChange ? handleConfirmToothChange : handleCloseModal
						}
						onClose={() => {
							setIsClosingModalOpened(false);
							setPendingToothChange(null);
						}}
					/>
				)}
			</Modal.Content>
		</Modal>
	);
};

export default DENTToothDetailModal;
